import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Phone, EnvelopeSimple, MapPin } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  PHONE, PHONE_HREF, EMAIL, EMAIL_HREF, SERVICE_OPTIONS, FREQUENCY_OPTIONS,
} from "@/data/content";

const EMPTY = { name: "", phone: "", email: "", service: "", frequency: "", address: "", message: "" };

export default function QuoteForm() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.service || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/quotes", form);
      toast.success("Quote request sent! We'll be in touch shortly.");
      setForm(EMPTY);
      navigate("/thank-you");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="quote" className="py-16 md:py-32 bg-secondary/40" data-testid="quote-section">
      <div className="max-w-6xl mx-auto px-6 md:px-10 grid lg:grid-cols-[0.85fr_1.15fr] gap-14 items-start">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-3">Free Estimate</p>
          <h2 className="font-heading text-4xl md:text-5xl font-medium tracking-tight text-primary">
            Tell us what you need cleaned
          </h2>
          <p className="mt-4 text-foreground/75 text-lg leading-relaxed">
            Complete the form and we'll contact you to discuss your cleaning needs and pricing.
          </p>

          <div className="mt-8 space-y-4">
            <a href={PHONE_HREF} className="flex items-center gap-4 rounded-2xl bg-card border border-border p-5 hover:-translate-y-0.5 hover:shadow-md transition-all" data-testid="contact-phone">
              <Phone size={24} weight="duotone" className="text-emerald-600" />
              <span><span className="block text-xs uppercase tracking-wide text-emerald-600 font-semibold">Phone</span><span className="font-semibold text-primary">{PHONE}</span></span>
            </a>
            <a href={EMAIL_HREF} className="flex items-center gap-4 rounded-2xl bg-card border border-border p-5 hover:-translate-y-0.5 hover:shadow-md transition-all" data-testid="contact-email">
              <EnvelopeSimple size={24} weight="duotone" className="text-emerald-600" />
              <span><span className="block text-xs uppercase tracking-wide text-emerald-600 font-semibold">Email</span><span className="font-semibold text-primary break-all">{EMAIL}</span></span>
            </a>
            <div className="flex items-center gap-4 rounded-2xl bg-card border border-border p-5">
              <MapPin size={24} weight="duotone" className="text-emerald-600" />
              <span><span className="block text-xs uppercase tracking-wide text-emerald-600 font-semibold">Service Area</span><span className="font-semibold text-primary">Everett and surrounding areas</span></span>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="rounded-3xl bg-card border border-border p-8 md:p-10 shadow-lg" data-testid="quote-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <Field id="name" label="Full Name *">
              <Input id="name" value={form.name} onChange={set("name")} placeholder="Your name" className="h-12" data-testid="input-name" />
            </Field>
            <Field id="phone" label="Phone Number *">
              <Input id="phone" type="tel" value={form.phone} onChange={set("phone")} placeholder="Your phone number" className="h-12" data-testid="input-phone" />
            </Field>
          </div>

          <div className="mt-5">
            <Field id="email" label="Email Address *">
              <Input id="email" type="email" value={form.email} onChange={set("email")} placeholder="Your email" className="h-12" data-testid="input-email" />
            </Field>
          </div>

          <div className="grid sm:grid-cols-2 gap-5 mt-5">
            <Field id="service" label="Cleaning Service *">
              <Select value={form.service} onValueChange={set("service")}>
                <SelectTrigger className="h-12 text-base" data-testid="select-service"><SelectValue placeholder="Select a service" /></SelectTrigger>
                <SelectContent>
                  {SERVICE_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field id="frequency" label="Frequency">
              <Select value={form.frequency} onValueChange={set("frequency")}>
                <SelectTrigger className="h-12 text-base" data-testid="select-frequency"><SelectValue placeholder="Select frequency" /></SelectTrigger>
                <SelectContent>
                  {FREQUENCY_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <div className="mt-5">
            <Field id="address" label="Property City or Address">
              <Input id="address" value={form.address} onChange={set("address")} placeholder="City or property address" className="h-12" data-testid="input-address" />
            </Field>
          </div>

          <div className="mt-5">
            <Field id="message" label="Cleaning Details *">
              <Textarea id="message" rows={5} value={form.message} onChange={set("message")} placeholder="Tell us about the property, number of rooms, bathrooms and anything requiring special attention." data-testid="input-message" />
            </Field>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="mt-7 w-full rounded-full bg-primary text-primary-foreground font-semibold py-4 hover:-translate-y-0.5 hover:shadow-lg transition-all disabled:opacity-60 disabled:hover:translate-y-0"
            data-testid="submit-quote-btn"
          >
            {loading ? "Sending…" : "Send Quote Request"}
          </button>
        </form>
      </div>
    </section>
  );
}

function Field({ id, label, children }) {
  return (
    <div>
      <Label htmlFor={id} className="text-sm font-semibold text-primary mb-2 block">{label}</Label>
      {children}
    </div>
  );
}
